'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useSiteConfig } from '@/lib/site-config';
import { useToast } from '@/lib/toast';
import { useConfirm } from '@/lib/confirm';

/**
 * Public comment thread under a prompt detail page. Single-level (no nested
 * replies in v1). Anyone can read; signed-in users can post; authors can
 * delete their own. Hidden + deleted entries render with a placeholder so
 * the thread doesn't visibly collapse mid-read.
 */
type CommentRow = {
  id: string;
  prompt_id: string;
  body: string;
  is_hidden: boolean;
  is_deleted: boolean;
  author: { id: string; username: string; name: string; avatar_url: string | null };
  created_at: string;
  updated_at: string;
};

const BODY_MAX = 2000;

export default function CommentThread({ promptId }: { promptId: string }) {
  const { user } = useAuth();
  const { config } = useSiteConfig();
  const commentsEnabled = config['comments.enabled'];
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<CommentRow[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.comments.list(promptId)
      .then((d) => { setRows(d.items); setNextCursor(d.next_cursor); setTotal(d.total); })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [promptId]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await api.comments.list(promptId, { cursor: nextCursor });
      setRows((prev) => [...(prev ?? []), ...d.items]);
      setNextCursor(d.next_cursor);
      setTotal(d.total);
    } finally { setLoadingMore(false); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const created = await api.comments.create(promptId, body);
      setRows((prev) => [...(prev ?? []), created]);
      setTotal((t) => t + 1);
      setText('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Couldn’t post');
    } finally { setSubmitting(false); }
  };

  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'Delete this comment?',
      body: 'It will be removed from the thread. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    }))) return;
    try {
      await api.comments.remove(id);
      // Backend now hides soft-deleted comments from the public list (no
      // "[deleted]" placeholder anymore — matches the user's expectation
      // that "delete = vanish"). Mirror that in local state by dropping
      // the row entirely instead of marking it is_deleted.
      setRows((prev) => prev?.filter((c) => c.id !== id) ?? null);
      setTotal((t) => Math.max(0, t - 1));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Couldn’t delete');
    }
  };

  const visibleCount = rows?.filter((c) => !c.is_deleted && !c.is_hidden).length ?? 0;

  return (
    <section style={{ marginTop: 48 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 600, margin: 0 }}>
          Discussion
        </h2>
        {!loading && rows && (
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {total > visibleCount ? `${visibleCount} of ${total}` : visibleCount} comment{total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Composer */}
      {!commentsEnabled ? (
        <div style={{
          padding: '14px 16px', marginBottom: 28,
          background: 'var(--bg-soft)', borderRadius: 'var(--radius-md)',
          fontSize: 13, color: 'var(--ink-3)', textAlign: 'center',
          border: '1px dashed var(--line)',
        }}>
          Comments are temporarily disabled by site admin. Existing replies stay visible.
        </div>
      ) : user ? (
        <form onSubmit={submit} style={{ marginBottom: 28 }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, BODY_MAX))}
            rows={3}
            placeholder="Share what worked / didn't, which model you tried, edits you'd suggest…"
            className="ps-input"
            style={{ resize: 'vertical', minHeight: 70 }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
            <span style={{
              fontSize: 11, color: text.length >= BODY_MAX - 50 ? '#f59e0b' : 'var(--ink-3)',
              fontVariantNumeric: 'tabular-nums',
            }}>{text.length} / {BODY_MAX}</span>
            <button
              type="submit"
              disabled={submitting || !text.trim()}
              aria-busy={submitting || undefined}
              className="ps-btn ps-btn-primary ps-btn-sm"
            >
              {submitting ? 'Posting…' : 'Post comment'}
            </button>
          </div>
        </form>
      ) : (
        <div style={{
          padding: '14px 16px', marginBottom: 28,
          background: 'var(--bg-soft)', borderRadius: 'var(--radius-md)',
          fontSize: 13, color: 'var(--ink-3)', textAlign: 'center',
        }}>
          <Link href="/auth/login" style={{ color: 'var(--accent)', fontWeight: 600 }}>Sign in</Link>{' '}
          to join the discussion.
        </div>
      )}

      {/* Thread */}
      {loading ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
      ) : !rows || rows.length === 0 ? (
        <div style={{
          padding: '32px 24px', textAlign: 'center',
          background: 'var(--bg-soft)', borderRadius: 'var(--radius-md)',
          color: 'var(--ink-3)', fontSize: 13,
        }}>
          Be the first to comment.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((c) => (
            <CommentCard
              key={c.id}
              comment={c}
              isOwn={!!user && user.id === c.author.id}
              onDelete={() => remove(c.id)}
            />
          ))}
          {nextCursor && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="ps-btn ps-btn-ghost ps-btn-sm"
              style={{ marginTop: 4, alignSelf: 'center' }}
            >
              {loadingMore ? 'Loading…' : `Load more (${total - rows.length} remaining)`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function CommentCard({ comment: c, isOwn, onDelete }: {
  comment: CommentRow;
  isOwn: boolean;
  onDelete: () => void;
}) {
  const muted = c.is_deleted || c.is_hidden;
  return (
    <article style={{
      display: 'flex', gap: 12,
      padding: 14,
      background: muted ? 'var(--bg-soft)' : 'var(--bg-raised)',
      border: '1px solid var(--line)',
      borderRadius: 'var(--radius-md)',
      opacity: muted ? 0.7 : 1,
    }}>
      <Link href={`/profile/${c.author.username}`} style={{ flexShrink: 0 }}>
        {c.author.avatar_url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={c.author.avatar_url} alt="" style={{
            width: 36, height: 36, borderRadius: '50%', objectFit: 'cover',
          }} />
        ) : (
          <span style={{
            display: 'grid', placeItems: 'center',
            width: 36, height: 36, borderRadius: '50%',
            background: 'var(--bg-soft)', color: 'var(--ink-2)',
            fontWeight: 600, fontSize: 14,
          }}>{c.author.name.slice(0, 1).toUpperCase()}</span>
        )}
      </Link>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <Link href={`/profile/${c.author.username}`} style={{
            fontWeight: 600, color: 'var(--ink)', textDecoration: 'none', fontSize: 13,
          }}>
            {c.author.name}
          </Link>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>@{c.author.username}</span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>·</span>
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }} title={new Date(c.created_at).toLocaleString()}>
            {fmtAgo(c.created_at)}
          </span>
        </div>
        <div style={{
          fontSize: 14, color: muted ? 'var(--ink-3)' : 'var(--ink)',
          fontStyle: muted ? 'italic' : 'normal',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{c.body}</div>
        {isOwn && !c.is_deleted && !c.is_hidden && (
          <button
            type="button"
            onClick={onDelete}
            style={{
              marginTop: 6, background: 'transparent', border: 0,
              color: 'var(--ink-3)', cursor: 'pointer', fontSize: 11, padding: 0,
            }}
          >
            Delete
          </button>
        )}
      </div>
    </article>
  );
}

function fmtAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
